import { useAuth0 } from '@auth0/auth0-react';
import { ShieldAlert } from 'lucide-react';

import { Button } from './ui/button';
import { Avatar, getAvatarInitials } from './ui/avatar';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface AccessDeniedProps {
    message?: string;
}

export const AccessDenied: React.FC<AccessDeniedProps> = ({
    message = "You don't have permission to access this application.",
}) => {
    const { logout, user } = useAuth0();
    useDocumentTitle('Access denied');

    return (
        <main className="flex min-h-screen w-full items-center justify-center bg-admin-app px-4 py-8">
            <div className="w-full max-w-md space-y-8 rounded-xl border bg-card p-6 shadow-lg sm:p-8">
                <div className="text-center space-y-6">
                    <div className="mb-6 flex justify-center">
                        <img src="/Horizontal-FullColor-1.svg" alt="WEVO" className="h-6 w-auto" />
                    </div>

                    <div className="space-y-4">
                        <ShieldAlert className="mx-auto size-8 text-red-600" aria-hidden />
                        <h1 className="text-2xl font-semibold leading-tight text-neutral-950">
                            Access denied
                        </h1>
                        <p className="text-base text-neutral-600">{message}</p>
                        <p className="text-sm text-neutral-500">
                            If you believe this is an error, please contact your system
                            administrator to request access to the management portal.
                        </p>
                    </div>
                </div>

                <div className="mt-8 space-y-3">
                    {user ? (
                        <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-left">
                            <Avatar
                                src={user.picture}
                                initials={getAvatarInitials(user.name, user.email)}
                                className="size-8 text-xs"
                            />
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-neutral-900">
                                    {user.name || user.email || 'User'}
                                </p>
                                {user.name && user.email ? (
                                    <p className="truncate text-xs text-neutral-500">
                                        {user.email}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    <Button
                        type="button"
                        size="lg"
                        className="w-full"
                        onClick={() =>
                            logout({ logoutParams: { returnTo: window.location.origin } })
                        }
                    >
                        Sign out
                    </Button>
                    <p className="text-sm text-center text-neutral-500">
                        You may try signing in with a different account that has administrator
                        privileges.
                    </p>
                </div>
            </div>
        </main>
    );
};
